import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { useDebounce } from '@/hooks/useDebounce'
import { FiMoreVertical, FiSearch, FiChevronLeft, FiChevronRight, FiChevronDown, FiX } from 'react-icons/fi'
import MetricCard from '@/components/ui/MetricCard'
import { FINANCE_TABS } from '@/constants/financeTabs'
import { getProductsServices, productToTableRow } from '@/services/api/finance'
import { getXeroItemDetail, getXeroBillPdfUrl, type XeroItemDetail } from '@/services/api/xero'
import PdfViewerModal from '@/pages/finance/PdfViewerModal'
import PageSizeSelect from '@/components/common/PageSizeSelect'

/** Xero product/service detail page (replace {id} with xeroItemId); tenant short code may vary per org */
const XERO_PRODUCTS_DETAIL_BASE = 'https://go.xero.com/app/!q98Pl/products-and-services'

type ProductRow = ReturnType<typeof productToTableRow>

const FinanceProductsServices = () => {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [timeframe, setTimeframe] = useState('This Month')
  const [filter, setFilter] = useState('All')
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '')
  const debouncedSearch = useDebounce(searchQuery, 400)
  const [productsData, setProductsData] = useState<ProductRow[]>([])
  const [productsLoading, setProductsLoading] = useState(true)
  const [productsTotal, setProductsTotal] = useState(0)
  const [productsError, setProductsError] = useState<string | null>(null)
  const [xeroDetailPopup, setXeroDetailPopup] = useState<{ url: string; name: string; xeroItemId: string | null } | null>(null)
  const [xeroItemDetail, setXeroItemDetail] = useState<{ item: NonNullable<XeroItemDetail['item']>; latestTransactions: XeroItemDetail['latestTransactions'] } | null>(null)
  const [xeroItemDetailLoading, setXeroItemDetailLoading] = useState(false)
  const [xeroItemDetailError, setXeroItemDetailError] = useState<string | null>(null)
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null)
  const actionMenuRef = useRef<HTMLDivElement | null>(null)
  const [billPdfState, setBillPdfState] = useState<{
    isOpen: boolean
    pdfUrl: string | null
    pdfLoading: boolean
    pdfErrorMessage: string | null
    pdfTitle: string
  }>({ isOpen: false, pdfUrl: null, pdfLoading: false, pdfErrorMessage: null, pdfTitle: 'Bill' })

  const [resultsPerPage, setResultsPerPage] = useState(25)

  const escapeCsv = (value: string | number) => {
    const stringValue = String(value ?? '')
    if (/[",\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`
    }
    return stringValue
  }

  const downloadCsv = useCallback((headers: string[], rows: Array<Array<string | number>>, filename: string) => {
    const csv = [headers.map(escapeCsv).join(','), ...rows.map((row) => row.map(escapeCsv).join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [])

  const handleExport = useCallback(() => {
    const rows = productsData.map((product) => [
      product.code,
      product.name,
      product.costPrice,
      product.salePrice,
      product.quantity,
    ])
    const datePart = new Date().toISOString().slice(0, 10)
    downloadCsv(
      ['Code', 'Name', 'Cost Price', 'Sale Price', 'Quantity'],
      rows,
      `products-services-${datePart}.csv`
    )
  }, [downloadCsv, productsData])

  const handleDeleteProduct = useCallback((productId: string, productName: string) => {
    const confirmed = window.confirm(`Delete "${productName}" from the list?`)
    if (!confirmed) return
    setProductsData((prev) => prev.filter((row) => row.id !== productId))
    setProductsTotal((prev) => Math.max(0, prev - 1))
    setOpenActionMenuId(null)
    setXeroDetailPopup((prev) => (prev?.name === productName ? null : prev))
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])

  const closeXeroPopup = useCallback(() => {
    setXeroDetailPopup(null)
    setXeroItemDetail(null)
    setXeroItemDetailError(null)
  }, [])
  useEffect(() => {
    if (!xeroDetailPopup) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeXeroPopup()
    }
    globalThis.addEventListener('keydown', onKeyDown)
    return () => globalThis.removeEventListener('keydown', onKeyDown)
  }, [xeroDetailPopup, closeXeroPopup])

  useEffect(() => {
    if (!openActionMenuId) return
    const onClickOutside = (event: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setOpenActionMenuId(null)
      }
    }
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenActionMenuId(null)
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onEscape)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onEscape)
    }
  }, [openActionMenuId])

  useEffect(() => {
    if (!xeroDetailPopup?.xeroItemId) {
      setXeroItemDetail(null)
      setXeroItemDetailError(null)
      return
    }
    setXeroItemDetailLoading(true)
    setXeroItemDetailError(null)
    getXeroItemDetail(xeroDetailPopup.xeroItemId)
      .then((res) => {
        setXeroItemDetail(res.item ? { item: res.item, latestTransactions: res.latestTransactions ?? [] } : null)
      })
      .catch((err: unknown) => {
        const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? (err as Error)?.message ?? 'Failed to load product details.'
        setXeroItemDetailError(msg)
        setXeroItemDetail(null)
      })
      .finally(() => setXeroItemDetailLoading(false))
  }, [xeroDetailPopup?.xeroItemId])

  useEffect(() => {
    setProductsLoading(true)
    setProductsError(null)
    getProductsServices({
      page: currentPage,
      limit: resultsPerPage,
      search: debouncedSearch || undefined,
    })
      .then(({ products, meta }) => {
        setProductsData(products.map(productToTableRow))
        setProductsTotal(meta?.total ?? products.length)
      })
      .catch((err: unknown) => {
        setProductsData([])
        setProductsTotal(0)
        setProductsError(err instanceof Error ? err.message : 'Couldn\'t load latest data.')
      })
      .finally(() => setProductsLoading(false))
  }, [currentPage, debouncedSearch, resultsPerPage])

  useEffect(() => {
    const q = searchParams.get('search')
    if (q !== null) setSearchQuery(q)
  }, [searchParams])

  const tabs = FINANCE_TABS

  const totalResults = productsTotal
  const totalPages = Math.max(1, totalResults === 0 ? 1 : Math.ceil(totalResults / resultsPerPage))
  const showingCount =
    totalResults === 0
      ? 0
      : Math.min(resultsPerPage, Math.max(0, totalResults - (currentPage - 1) * resultsPerPage))
  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const pages: number[] = [1]
    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)
    if (start > 2) pages.push(-1)
    for (let p = start; p <= end; p++) if (!pages.includes(p)) pages.push(p)
    if (end < totalPages - 1) pages.push(-2)
    if (totalPages > 1) pages.push(totalPages)
    return pages
  }
  const pageNumbers = getPageNumbers()

  return (
    <div className="space-y-6" style={{ maxWidth: '100%' }}>
      {/* Header Section */}
      <div>
        {/* First Row: Finance title and Breadcrumbs */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
          <h1
            className="text-lg sm:text-xl"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: 'clamp(18px, 4vw, 20px)',
              lineHeight: '28px',
              letterSpacing: '0%',
              color: '#212126',
            }}
          >
            Products and Services
          </h1>
          
          {/* Breadcrumbs on the right of Finance title */}
          <span
            className="text-xs sm:text-sm"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: 'clamp(11px, 2.5vw, 12px)',
              lineHeight: '28px',
              letterSpacing: '0%',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ color: '#69697A', margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Products and Services</span>
          </span>
        </div>

        {/* Second Row: Tabs and Controls on the same level */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Tabs - full width so all tabs (including Business Analytics) are visible */}
          <div 
            className="flex items-center overflow-x-auto scrollbar-hide flex-1 min-w-0"
            style={{
              width: '100%',
              minWidth: 0,
              height: '38px',
              borderRadius: '8px',
              border: '1px solid #E9E9EA',
              backgroundColor: '#FFFFFF',
              padding: '4px',
              gap: '0px'
            }}
          >
            {tabs.map((tab) => {
              const isActive = location.pathname === tab.path
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    color: isActive ? '#FFFFFF' : '#69697A',
                    backgroundColor: isActive ? '#212126' : 'transparent',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    justifyContent: 'center',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {tab.label}
                </Link>
              )
            })}
          </div>

          {/* This Month and Export */}
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0" style={{ height: '40px' }}>
            {/* This Month Dropdown */}
            <div className="relative">
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="px-3 sm:px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none text-sm sm:text-base"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: 'clamp(12px, 3vw, 14px)',
                  lineHeight: '20px',
                  letterSpacing: '0%',
                  color: '#050521',
                  height: '40px',
                  minWidth: '120px',
                }}
              >
                <option>This Month</option>
                <option>Last Month</option>
                <option>This Year</option>
              </select>
              <FiChevronDown 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                style={{
                  width: '16px',
                  height: '16px',
                  opacity: 0.8,
                  color: '#050521'
                }}
              />
            </div>
            {/* Export Button */}
            <button
              type="button"
              onClick={handleExport}
              className="flex items-center justify-center gap-2 sm:gap-3 font-medium rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
              style={{
                minWidth: '100px',
                width: 'auto',
                height: '38px',
                color: '#212126',
                border: '1px solid #212126',
                padding: '8px 16px',
                borderRadius: '6px',
              }}
            >
              <img 
                src="/images/download.svg" 
                alt="Download" 
                className="w-4 h-4" 
                style={{ 
                  filter: 'brightness(0) saturate(100%) invert(8%) sepia(4%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(95%)'
                }} 
              />
              <span className="text-sm sm:text-base">Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards - synced with products/services table data */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <MetricCard
          title="Total Items"
          value={productsLoading ? '…' : String(totalResults)}
          trend=""
          icon={
            <img 
              src="/images/note-2.svg" 
              alt="Total Items" 
              style={{
                width: '20px',
                height: '20px'
              }}
            />
          }
          iconBgColor=""
          iconBgColorCustom="#E8F7EF"
          style={{ width: '100%' }}
        />
        <MetricCard
          title="Total Products"
          value={productsLoading ? '…' : String(totalResults)}
          trend=""
          icon={
            <img 
              src="/images/box.svg" 
              alt="Total Products" 
              className="w-5 h-5"
              style={{
                filter: 'brightness(0) saturate(100%) invert(8%) sepia(100%) saturate(2000%) hue-rotate(100deg) brightness(0.1) contrast(1.2)'
              }}
            />
          }
          iconBgColor=""
          iconBgColorCustom="#F2F4F2"
          style={{ width: '100%' }}
        />
        <MetricCard
          title="Total Services"
          value={productsLoading ? '…' : '0'}
          trend=""
          icon={
            <img 
              src="/images/receipt-2.svg" 
              alt="Total Services" 
              className="w-5 h-5"
            />
          }
          iconBgColor=""
          iconBgColorCustom="#EAF1FF"
          style={{ width: '100%' }}
        />
      </div>
      {productsError && (
        <p className="text-sm text-amber-600" style={{ fontFamily: 'Lexend Deca' }}>
          {productsError} Showing 0 for all metrics.
        </p>
      )}

      {/* Products and Services Table Section */}
      <div className="bg-white rounded-lg" style={{ paddingBottom: '24px' }}>
        <div className="p-4 sm:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-2 gap-4">
            <h3
              className="text-sm sm:text-base"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontStyle: 'normal',
                fontSize: 'clamp(13px, 3vw, 14px)',
                lineHeight: '28px',
                letterSpacing: '0%',
                color: '#212126',
              }}
            >
              Products and Services
            </h3>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
              {/* Search Bar */}
              <div className="relative">
                <FiSearch 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{
                    width: '18px',
                    height: '18px',
                    opacity: 1,
                    color: '#69697A'
                  }}
                />
                <input
                  type="text"
                  placeholder="Search Products and Services"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0%',
                    color: '#212126',
                    width: '236px',
                    height: '34px',
                    borderRadius: '8px',
                    border: '1px solid #E9E9EA',
                    padding: '10px 12px 10px 40px',
                    background: '#FFFFFF',
                  }}
                />
              </div>
              {/* All Filter Dropdown */}
              <div className="relative">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0%',
                    color: '#212126',
                    height: '34px',
                    minWidth: '80px',
                  }}
                >
                  <option>All</option>
                  <option>Products</option>
                  <option>Services</option>
                </select>
                <FiChevronDown 
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{
                    width: '16px',
                    height: '16px',
                    opacity: 0.8,
                    color: '#212126'
                  }}
                />
              </div>
              {/* Export Button */}
              <button
                type="button"
                onClick={handleExport}
                className="flex items-center justify-center px-4 py-2 rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontStyle: 'normal',
                  fontSize: '12px',
                  lineHeight: '20px',
                  letterSpacing: '0%',
                  color: '#FFFFFF',
                  backgroundColor: '#212126',
                  borderRadius: '6px',
                  height: '34px',
                  minWidth: '80px',
                }}
              >
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div 
          className="overflow-x-auto -mx-4 sm:mx-6"
          style={{
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #E9E9EA',
            marginBottom: '32px',
          }}
        >
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E9E9EA' }}>
                <th
                  className="text-left py-3"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    color: '#69697A',
                    borderLeft: 'none',
                    borderRight: 'none',
                    paddingLeft: '16px',
                    borderTopLeftRadius: '8px',
                  }}
                >
                  Code
                </th>
                <th
                  className="text-left py-3"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    color: '#69697A',
                    borderLeft: 'none',
                    borderRight: 'none',
                    paddingLeft: '16px',
                  }}
                >
                  Name
                </th>
                <th
                  className="text-left py-3"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    color: '#69697A',
                    borderLeft: 'none',
                    borderRight: 'none',
                    paddingLeft: '16px',
                  }}
                >
                  Cost price
                </th>
                <th
                  className="text-left py-3"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    color: '#69697A',
                    borderLeft: 'none',
                    borderRight: 'none',
                    paddingLeft: '16px',
                  }}
                >
                  Sale price
                </th>
                <th
                  className="text-left py-3"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    color: '#69697A',
                    borderLeft: 'none',
                    borderRight: 'none',
                    paddingLeft: '16px',
                  }}
                >
                  Quantity
                </th>
                <th
                  className="text-left py-3"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: '0%',
                    color: '#69697A',
                    borderLeft: 'none',
                    borderRight: 'none',
                    paddingLeft: '16px',
                    borderTopRightRadius: '8px',
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {productsLoading ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center" style={{ fontFamily: 'Lexend Deca', fontSize: '14px', color: '#69697A', borderBottom: '1px solid #E9E9EA' }}>
                    Loading…
                  </td>
                </tr>
              ) : productsData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center" style={{ fontFamily: 'Lexend Deca', fontSize: '14px', color: '#69697A', borderBottom: '1px solid #E9E9EA' }}>
                    No products or services yet. Sync from Xero (Settings → Integrations) or add one.
                  </td>
                </tr>
              ) : (
                productsData.map((product, index) => {
                const isLastRow = index === productsData.length - 1
                const xeroDetailUrl = product.xeroItemId
                  ? `${XERO_PRODUCTS_DETAIL_BASE}/${product.xeroItemId}`
                  : XERO_PRODUCTS_DETAIL_BASE
                const isActionMenuOpen = openActionMenuId === product.id
                return (
                  <tr 
                    key={product.id}
                    style={{
                      borderBottom: isLastRow ? 'none' : '1px solid #E9E9EA',
                      cursor: 'pointer',
                    }}
                    onClick={() => setXeroDetailPopup({ url: xeroDetailUrl, name: product.name, xeroItemId: product.xeroItemId ?? null })}
                  >
                    <td
                      className="py-3"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '12px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#212126',
                        borderLeft: 'none',
                        borderRight: 'none',
                        paddingLeft: '16px',
                        ...(isLastRow && { borderBottomLeftRadius: '8px' }),
                      }}
                    >
                      {product.code}
                    </td>
                    <td
                      className="py-3"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '12px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#212126',
                        borderLeft: 'none',
                        borderRight: 'none',
                        paddingLeft: '16px',
                      }}
                    >
                      {product.name}
                    </td>
                    <td
                      className="py-3"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '12px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#212126',
                        borderLeft: 'none',
                        borderRight: 'none',
                        paddingLeft: '16px',
                      }}
                    >
                      {product.costPrice}
                    </td>
                    <td
                      className="py-3"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '12px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#212126',
                        borderLeft: 'none',
                        borderRight: 'none',
                        paddingLeft: '16px',
                      }}
                    >
                      {product.salePrice}
                    </td>
                    <td
                      className="py-3"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '12px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#212126',
                        borderLeft: 'none',
                        borderRight: 'none',
                        paddingLeft: '16px',
                      }}
                    >
                      {product.quantity}
                    </td>
                    <td
                      className="py-3"
                      style={{
                        borderLeft: 'none',
                        borderRight: 'none',
                        paddingLeft: '16px',
                        position: 'relative',
                        ...(isLastRow && { borderBottomRightRadius: '8px' }),
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button 
                        type="button"
                        className="rounded flex items-center justify-center"
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '4px',
                          backgroundColor: '#EAF1FF',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          setOpenActionMenuId((prev) => (prev === product.id ? null : product.id))
                        }}
                      >
                        <FiMoreVertical style={{ width: '16px', height: '16px', color: '#2C71F6' }} />
                      </button>
                      {isActionMenuOpen && (
                        <div
                          ref={actionMenuRef}
                          role="menu"
                          style={{
                            position: 'absolute',
                            right: '16px',
                            top: '42px',
                            minWidth: '160px',
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #E9E9EA',
                            borderRadius: '8px',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
                            zIndex: 30,
                            overflow: 'hidden',
                          }}
                        >
                          <button
                            type="button"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation()
                              setOpenActionMenuId(null)
                              setXeroDetailPopup({ url: xeroDetailUrl, name: product.name, xeroItemId: product.xeroItemId ?? null })
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50"
                            style={{ fontFamily: 'Lexend Deca', fontSize: '13px', color: '#212126' }}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation()
                              setOpenActionMenuId(null)
                              window.open(xeroDetailUrl, '_blank', 'noopener,noreferrer')
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50"
                            style={{ fontFamily: 'Lexend Deca', fontSize: '13px', color: '#212126' }}
                          >
                            Open in Xero
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteProduct(product.id, product.name)
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50"
                            style={{ fontFamily: 'Lexend Deca', fontSize: '13px', color: '#E93323' }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })
              )}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan={6}
                  className="py-4"
                  style={{
                    borderTop: '1px solid #E9E9EA',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    paddingBottom: '20px',
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0%',
                        color: '#69697A',
                      }}
                    >
                      Showing {totalResults === 0 ? 0 : (currentPage - 1) * resultsPerPage + 1}–{(currentPage - 1) * resultsPerPage + showingCount} of {totalResults}
                    </span>
                    <div className="flex items-center gap-2">
                    <PageSizeSelect
                      value={resultsPerPage}
                      onChange={(size) => {
                        setResultsPerPage(size)
                        setCurrentPage(1)
                      }}
                    />
                      <button
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage === 1 || productsLoading}
                        className="flex items-center gap-2 px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontStyle: 'normal',
                          fontSize: '14px',
                          lineHeight: '20px',
                          letterSpacing: '0%',
                          color: '#212126',
                        }}
                      >
                        <FiChevronLeft style={{ width: '16px', height: '16px' }} />
                        <span>Previous</span>
                      </button>
                      {pageNumbers.map((page) =>
                        page < 0 ? (
                          <span
                            key={page === -1 ? 'ellipsis-start' : 'ellipsis-end'}
                            className="px-2"
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontSize: '14px',
                              color: '#69697A',
                            }}
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            disabled={productsLoading}
                            className="px-3 py-1 border rounded"
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontWeight: 400,
                              fontStyle: 'normal',
                              fontSize: '14px',
                              lineHeight: '20px',
                              letterSpacing: '0%',
                              color: currentPage === page ? '#FFFFFF' : '#212126',
                              backgroundColor: currentPage === page ? '#212126' : '#FFFFFF',
                              borderColor: currentPage === page ? '#212126' : '#E9E9EA',
                            }}
                          >
                            {page}
                          </button>
                        )
                      )}
                      <button
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages || productsLoading}
                        className="flex items-center gap-2 px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontStyle: 'normal',
                          fontSize: '14px',
                          lineHeight: '20px',
                          letterSpacing: '0%',
                          color: '#212126',
                        }}
                      >
                        <span>Next</span>
                        <FiChevronRight style={{ width: '16px', height: '16px' }} />
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Xero product detail popup */}
      {xeroDetailPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={closeXeroPopup}
        >
          <dialog
            open
            className="bg-white rounded-lg shadow-xl flex flex-col border-0 p-0 w-full max-w-[1000px]"
            style={{
              width: 'min(95vw, 1000px)',
              height: 'min(85vh, 800px)',
              maxHeight: '85vh',
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Product details: ${xeroDetailPopup.name}`}
          >
            <div
              className="flex items-center justify-between shrink-0 px-4 py-3 border-b"
              style={{
                borderColor: '#E9E9EA',
                fontFamily: 'Lexend Deca',
                fontWeight: 600,
                fontSize: '16px',
                color: '#212126',
              }}
            >
              <span>{xeroDetailPopup.name} – Xero</span>
              <button
                type="button"
                onClick={closeXeroPopup}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                style={{ color: '#212126' }}
                aria-label="Close"
              >
                <FiX style={{ width: '24px', height: '24px' }} />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {xeroItemDetailLoading ? (
                <div className="flex items-center justify-center p-12" style={{ fontFamily: 'Lexend Deca', color: '#69697A', fontSize: '14px' }}>
                  Loading product details…
                </div>
              ) : xeroItemDetailError ? (
                <div className="p-6 text-center" style={{ fontFamily: 'Lexend Deca', color: '#69697A', fontSize: '14px' }}>
                  <p className="mb-4">{xeroItemDetailError}</p>
                  <button
                    type="button"
                    onClick={() => window.open(xeroDetailPopup.url, '_blank', 'noopener,noreferrer')}
                    className="px-4 py-2 rounded-lg font-semibold"
                    style={{ backgroundColor: '#212126', color: '#FFFFFF', fontFamily: 'Lexend Deca', fontSize: '14px' }}
                  >
                    Open in Xero (new tab)
                  </button>
                </div>
              ) : xeroItemDetail ? (
                <div className="p-6" style={{ fontFamily: 'Lexend Deca', fontSize: '14px', color: '#212126', lineHeight: '22px' }}>
                  <div className="space-y-6">
                    <section>
                      <h4 className="font-semibold mb-2" style={{ color: '#212126', fontSize: '13px' }}>Purchase</h4>
                      <dl className="space-y-1.5">
                        <div className="flex gap-2">
                          <dt className="text-gray-500 min-w-[120px]">Cost price</dt>
                          <dd>{xeroItemDetail.item.purchaseDetails?.unitPrice != null ? Number(xeroItemDetail.item.purchaseDetails.unitPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</dd>
                        </div>
                        <div className="flex gap-2">
                          <dt className="text-gray-500 min-w-[120px]">Purchases account</dt>
                          <dd>{xeroItemDetail.item.purchaseDetails?.accountCode ?? '—'}</dd>
                        </div>
                        <div className="flex gap-2">
                          <dt className="text-gray-500 min-w-[120px]">Tax rate</dt>
                          <dd>{xeroItemDetail.item.purchaseDetails?.taxType ?? '—'}</dd>
                        </div>
                        <div className="flex gap-2">
                          <dt className="text-gray-500 min-w-[120px]">Description</dt>
                          <dd>{xeroItemDetail.item.description ?? xeroItemDetail.item.purchaseDescription ?? xeroItemDetail.item.name ?? '—'}</dd>
                        </div>
                      </dl>
                    </section>
                    {(xeroItemDetail.item.salesDetails?.unitPrice != null || xeroItemDetail.item.salesDetails?.accountCode) && (
                      <section>
                        <h4 className="font-semibold mb-2" style={{ color: '#212126', fontSize: '13px' }}>Sales</h4>
                        <dl className="space-y-1.5">
                          <div className="flex gap-2">
                            <dt className="text-gray-500 min-w-[120px]">Sale price</dt>
                            <dd>{xeroItemDetail.item.salesDetails?.unitPrice != null ? Number(xeroItemDetail.item.salesDetails.unitPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</dd>
                          </div>
                          <div className="flex gap-2">
                            <dt className="text-gray-500 min-w-[120px]">Sales account</dt>
                            <dd>{xeroItemDetail.item.salesDetails?.accountCode ?? '—'}</dd>
                          </div>
                        </dl>
                      </section>
                    )}
                    {xeroItemDetail.item.quantityOnHand != null && (
                      <section>
                        <h4 className="font-semibold mb-2" style={{ color: '#212126', fontSize: '13px' }}>Quantity on hand</h4>
                        <p>{Number(xeroItemDetail.item.quantityOnHand)}</p>
                      </section>
                    )}
                    <section>
                      <h4 className="font-semibold mb-2" style={{ color: '#212126', fontSize: '13px' }}>Latest transactions</h4>
                      {xeroItemDetail.latestTransactions.length === 0 ? (
                        <p style={{ color: '#69697A', fontSize: '13px' }}>No bills found for this product yet.</p>
                      ) : (
                        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: '#E9E9EA' }}>
                          <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#F9F9F9', borderBottom: '1px solid #E9E9EA' }}>
                                <th className="text-left py-2 px-3" style={{ color: '#69697A', fontWeight: 500 }}>Issue Date</th>
                                <th className="text-left py-2 px-3" style={{ color: '#69697A', fontWeight: 500 }}>Type</th>
                                <th className="text-left py-2 px-3" style={{ color: '#69697A', fontWeight: 500 }}>Reference</th>
                                <th className="text-right py-2 px-3" style={{ color: '#69697A', fontWeight: 500 }}>Quantity</th>
                                <th className="text-right py-2 px-3" style={{ color: '#69697A', fontWeight: 500 }}>Price</th>
                                <th className="text-right py-2 px-3" style={{ color: '#69697A', fontWeight: 500 }}>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {xeroItemDetail.latestTransactions.map((tx, idx) => (
                                <tr key={`${tx.invoiceID}-${idx}`} style={{ borderBottom: '1px solid #E9E9EA' }}>
                                  <td className="py-2 px-3">{tx.date}</td>
                                  <td className="py-2 px-3">{tx.type}</td>
                                    <td className="py-2 px-3">
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          setBillPdfState((s) => ({ ...s, isOpen: true, pdfUrl: null, pdfErrorMessage: null, pdfTitle: `Bill – ${tx.reference || tx.invoiceNumber}`, pdfLoading: true }))
                                          try {
                                            const blobUrl = await getXeroBillPdfUrl(tx.invoiceID)
                                            setBillPdfState((s) => ({ ...s, pdfUrl: blobUrl, pdfLoading: false, pdfErrorMessage: null }))
                                          } catch (err: unknown) {
                                            const res = (err as { response?: { data?: Blob; status?: number } })?.response
                                            let message = 'Could not load the bill PDF.'
                                            if (res?.data instanceof Blob) {
                                              try {
                                                const text = await res.data.text()
                                                const j = JSON.parse(text) as { error?: { message?: string }; message?: string }
                                                message = j?.error?.message ?? j?.message ?? message
                                              } catch {
                                                if (res.status === 404) message = 'This bill has no PDF in Xero or the link is missing.'
                                              }
                                            }
                                            setBillPdfState((s) => ({ ...s, pdfUrl: null, pdfLoading: false, pdfErrorMessage: message }))
                                          }
                                        }}
                                        className="underline hover:no-underline text-left"
                                        style={{ color: '#2C71F6', cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                                      >
                                        {tx.reference || tx.invoiceNumber}
                                      </button>
                                    </td>
                                  <td className="py-2 px-3 text-right">{tx.quantity}</td>
                                  <td className="py-2 px-3 text-right">{tx.unitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  <td className="py-2 px-3 text-right">{tx.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </section>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-8 text-center" style={{ fontFamily: 'Lexend Deca', color: '#69697A', fontSize: '14px' }}>
                  <p>Product details are only available when synced from Xero. Sync Products &amp; Services in Settings → Integrations, then try again.</p>
                </div>
              )}
            </div>
          </dialog>
        </div>
      )}

      <PdfViewerModal
        isOpen={billPdfState.isOpen}
        onClose={() => {
          if (billPdfState.pdfUrl && billPdfState.pdfUrl.startsWith('blob:')) {
            URL.revokeObjectURL(billPdfState.pdfUrl)
          }
          setBillPdfState({ isOpen: false, pdfUrl: null, pdfLoading: false, pdfErrorMessage: null, pdfTitle: 'Bill' })
        }}
        pdfUrl={billPdfState.pdfUrl}
        title={billPdfState.pdfTitle}
        isLoading={billPdfState.pdfLoading}
        errorMessage={billPdfState.pdfErrorMessage}
      />
    </div>
  )
}

export default FinanceProductsServices
